import React, { useDeferredValue, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronRight,
  FolderTree,
  Link2,
  Search,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  appRouteCatalog,
  formatSegmentLabel,
  getRouteGroupKey,
  getRouteLabel,
  isDynamicRoute,
  routeGroupMeta,
  routeGroupOrder,
} from "../data/appRouteCatalog";
import "../styles/SiteMapDirectory.css";

const QUICK_LINK_PATHS = [
  "/",
  "/utah-at-a-glance",
  "/verticals",
  "/search",
  "/about-us",
  "/contact-us",
  "/gallery",
  "/faq",
];

const sortByLabel = (leftPath, rightPath) =>
  getRouteLabel(leftPath).localeCompare(getRouteLabel(rightPath));

const buildNestedTree = (routes, rootPath) => {
  const rootSegments = rootPath.split("/").filter(Boolean);
  const root = {
    id: rootPath,
    label: getRouteLabel(rootPath),
    path: rootPath,
    routePath: rootPath,
    dynamic: false,
    children: [],
  };

  routes
    .filter((routePath) => routePath !== rootPath)
    .sort((leftPath, rightPath) => {
      const leftDepth = leftPath.split("/").filter(Boolean).length;
      const rightDepth = rightPath.split("/").filter(Boolean).length;
      return leftDepth - rightDepth || leftPath.localeCompare(rightPath);
    })
    .forEach((routePath) => {
      const routeSegments = routePath.split("/").filter(Boolean);
      const localSegments = routeSegments.slice(rootSegments.length);

      let parentNode = root;
      let currentPath = rootPath;

      localSegments.forEach((segment, index) => {
        currentPath = `${currentPath}/${segment}`;
        let childNode = parentNode.children.find((item) => item.id === currentPath);

        if (!childNode) {
          childNode = {
            id: currentPath,
            label:
              index === localSegments.length - 1
                ? getRouteLabel(routePath)
                : formatSegmentLabel(segment),
            path: null,
            routePath: currentPath,
            dynamic: segment.startsWith(":"),
            children: [],
          };
          parentNode.children.push(childNode);
        }

        if (index === localSegments.length - 1) {
          childNode.label = getRouteLabel(routePath);
          childNode.routePath = routePath;
          childNode.dynamic = isDynamicRoute(routePath);
          childNode.path = childNode.dynamic ? null : routePath;
        }

        parentNode = childNode;
      });
    });

  const sortNodes = (nodes) => {
    nodes.sort((leftNode, rightNode) => leftNode.label.localeCompare(rightNode.label));
    nodes.forEach((node) => sortNodes(node.children));
  };

  sortNodes(root.children);
  return root;
};

const filterNestedNode = (node, query) => {
  if (!query) {
    return node;
  }

  const normalizedQuery = query.toLowerCase();
  const childMatches = node.children
    .map((childNode) => filterNestedNode(childNode, normalizedQuery))
    .filter(Boolean);

  const selfMatches =
    node.label.toLowerCase().includes(normalizedQuery) ||
    (node.routePath || "").toLowerCase().includes(normalizedQuery);

  if (selfMatches || childMatches.length) {
    return {
      ...node,
      children: childMatches,
    };
  }

  return null;
};

const buildSections = (query) =>
  routeGroupOrder
    .map((groupKey) => {
      const groupMeta = routeGroupMeta[groupKey];
      const groupRoutes = appRouteCatalog
        .filter((routePath) => getRouteGroupKey(routePath) === groupKey)
        .sort(sortByLabel);

      if (!groupRoutes.length) {
        return null;
      }

      if (groupMeta.nested && groupMeta.rootPath) {
        const tree = buildNestedTree(groupRoutes, groupMeta.rootPath);
        const filteredTree = filterNestedNode(tree, query);

        if (!filteredTree) {
          return null;
        }

        return {
          key: groupKey,
          meta: groupMeta,
          routeCount: groupRoutes.length,
          tree: filteredTree,
          items: [],
        };
      }

      const items = groupRoutes
        .map((routePath) => ({
          id: routePath,
          label: getRouteLabel(routePath),
          path: isDynamicRoute(routePath) ? null : routePath,
          routePath,
          dynamic: isDynamicRoute(routePath),
        }))
        .filter((item) => {
          if (!query) {
            return true;
          }

          const normalizedQuery = query.toLowerCase();
          return (
            item.label.toLowerCase().includes(normalizedQuery) ||
            item.routePath.toLowerCase().includes(normalizedQuery)
          );
        });

      if (!items.length) {
        return null;
      }

      return {
        key: groupKey,
        meta: groupMeta,
        routeCount: groupRoutes.length,
        tree: null,
        items,
      };
    })
    .filter(Boolean);

const countNestedLinks = (node) =>
  (node.path ? 1 : 0) + node.children.reduce((sum, child) => sum + countNestedLinks(child), 0);

const NestedMenuNode = ({ node, depth = 0 }) => {
  const hasChildren = node.children.length > 0;
  const nodeClass = `sitemap-directory-node depth-${Math.min(depth, 4)}`;

  return (
    <div className={nodeClass}>
      <div className="sitemap-directory-nodeRow">
        <div className="sitemap-directory-nodeText">
          <span className="sitemap-directory-nodeLabel">{node.label}</span>
          <span className="sitemap-directory-nodePath">{node.routePath}</span>
        </div>

        {node.path ? (
          <Link to={node.path} className="sitemap-directory-nodeLink">
            <span>Open</span>
            <ArrowRight size={14} />
          </Link>
        ) : node.dynamic ? (
          <span className="sitemap-directory-nodeBadge">Dynamic</span>
        ) : null}
      </div>

      {hasChildren ? (
        <div className="sitemap-directory-nodeChildren">
          {node.children.map((childNode) => (
            <NestedMenuNode key={childNode.id} node={childNode} depth={depth + 1} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

function SiteMapDirectory() {
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearchTerm = useDeferredValue(searchTerm.trim());

  const quickLinks = useMemo(
    () =>
      QUICK_LINK_PATHS.map((path) => ({
        path,
        label: getRouteLabel(path),
      })),
    [],
  );

  const visibleSections = useMemo(
    () => buildSections(deferredSearchTerm),
    [deferredSearchTerm],
  );

  const visibleLinkCount = useMemo(
    () =>
      visibleSections.reduce((total, section) => {
        if (section.tree) {
          return total + countNestedLinks(section.tree);
        }

        return total + section.items.length;
      }, 0),
    [visibleSections],
  );

  return (
    <div className="sitemap-directory-page">
      <Header />

      <main className="sitemap-directory-main">
        <section className="sitemap-directory-shell">
          <div className="sitemap-directory-stats">
              <div className="sitemap-directory-statCard">
                <strong>{appRouteCatalog.length}</strong>
                <span>Total pages</span>
              </div>
            <div className="sitemap-directory-statCard">
              <strong>{visibleLinkCount}</strong>
              <span>Visible links</span>
            </div>
          </div>

          <div className="sitemap-directory-toolbar">
            <label className="sitemap-directory-search">
              <Search size={17} />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search page names or links"
                aria-label="Search structured sitemap"
              />
            </label>

            <div className="sitemap-directory-quickBar">
              {quickLinks.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="sitemap-directory-quickLink"
                >
                  <Link2 size={13} />
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          </div>

          <div className="sitemap-directory-grid">
            {visibleSections.map((section) => {
              const { meta } = section;

              return (
                <section
                  key={section.key}
                  className="sitemap-directory-panel"
                  style={{
                    "--section-accent": meta.accent,
                    "--section-soft": meta.soft,
                    "--section-glow": meta.glow,
                  }}
                >
                  <div className="sitemap-directory-panelHeader">
                    <div>
                      <span className="sitemap-directory-panelTag">{meta.label}</span>
                      <h2>{meta.description}</h2>
                    </div>
                    <div className="sitemap-directory-panelMeta">
                      <FolderTree size={16} />
                      <span>{section.routeCount} pages</span>
                    </div>
                  </div>

                  {section.tree ? (
                    <div className="sitemap-directory-tree">
                      <div className="sitemap-directory-treeRoot">
                        <div className="sitemap-directory-rootCard">
                          <div>
                            <strong>{section.tree.label}</strong>
                            <span>{section.tree.routePath}</span>
                          </div>
                          {section.tree.path ? (
                            <Link to={section.tree.path} className="sitemap-directory-rootAction">
                              <span>Open</span>
                              <ChevronRight size={15} />
                            </Link>
                          ) : null}
                        </div>
                      </div>

                      <div className="sitemap-directory-treeBody">
                        {section.tree.children.map((childNode) => (
                          <NestedMenuNode key={childNode.id} node={childNode} />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="sitemap-directory-links">
                      {section.items.map((item) =>
                        item.path ? (
                          <Link
                            key={item.id}
                            to={item.path}
                            className="sitemap-directory-linkCard"
                          >
                            <div>
                              <strong>{item.label}</strong>
                              <span>{item.routePath}</span>
                            </div>
                            <ArrowRight size={14} />
                          </Link>
                        ) : (
                          <div key={item.id} className="sitemap-directory-linkCard passive">
                            <div>
                              <strong>{item.label}</strong>
                              <span>{item.routePath}</span>
                            </div>
                            <span className="sitemap-directory-nodeBadge">Dynamic</span>
                          </div>
                        ),
                      )}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default SiteMapDirectory;
